import React, { useState, useEffect, useMemo } from 'react';
import { Send, Activity, ShieldCheck, Mail, MapPin, Phone, AlertCircle, CheckCircle2, Check } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useLanguage } from '../context/LanguageContext';

interface FormState {
  name: string;
  email: string;
  phone: string;
  projectType: string;
  message: string;
}

interface TouchedState {
  name: boolean;
  email: boolean;
  phone: boolean;
  projectType: boolean;
  message: boolean;
}

const Contact: React.FC = () => {
  const { t, isUrdu } = useLanguage();
  const [formState, setFormState] = useState<FormState>({ name: '', email: '', phone: '', projectType: '', message: '' });
  const [touched, setTouched] = useState<TouchedState>({ name: false, email: false, phone: false, projectType: false, message: false });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handlePopulate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setFormState((prev) => ({
          ...prev,
          projectType: customEvent.detail.projectType || '',
          message: customEvent.detail.message || '',
        }));
        setTouched((prev) => ({
          ...prev,
          projectType: true,
          message: true,
        }));
      }
    };
    window.addEventListener('populateForm', handlePopulate);
    return () => window.removeEventListener('populateForm', handlePopulate);
  }, []);

  // Real-time validation rules
  const validation = useMemo(() => {
    // Name validation: At least 2 characters
    const trimmedName = formState.name.trim();
    const isNameValid = trimmedName.length >= 2;
    let nameError = '';
    if (!trimmedName) {
      nameError = isUrdu ? 'پورا نام درج کرنا لازمی ہے' : 'Full name is required';
    } else if (trimmedName.length < 2) {
      nameError = isUrdu ? 'نام کم از کم 2 حروف پر مشتمل ہونا چاہیے' : 'Name must be at least 2 characters';
    }

    // Email validation: Standard RFC email format
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const trimmedEmail = formState.email.trim();
    const isEmailValid = emailRegex.test(trimmedEmail);
    let emailError = '';
    if (!trimmedEmail) {
      emailError = isUrdu ? 'ای میل ایڈریس لازمی ہے' : 'Email address is required';
    } else if (!isEmailValid) {
      emailError = isUrdu ? 'براہ کرم درست ای میل درج کریں (مثال: name@domain.com)' : 'Please enter a valid email (e.g. name@domain.com)';
    }

    // Phone validation (optional, but if provided must be >= 7 valid digits/symbols)
    const trimmedPhone = formState.phone.trim();
    let isPhoneValid = true;
    let phoneError = '';
    if (trimmedPhone) {
      const digitsOnly = trimmedPhone.replace(/\D/g, '');
      const validPhoneFormat = /^[+]?[0-9\s().-]{7,20}$/.test(trimmedPhone);
      if (!validPhoneFormat || digitsOnly.length < 7) {
        isPhoneValid = false;
        phoneError = isUrdu ? 'براہ کرم درست فون نمبر درج کریں (کم از کم 7 ہندسے)' : 'Please enter a valid phone number (minimum 7 digits)';
      }
    }

    // Message validation: Minimum 10 characters for a constructive inquiry
    const trimmedMessage = formState.message.trim();
    const isMessageValid = trimmedMessage.length >= 10;
    let messageError = '';
    if (!trimmedMessage) {
      messageError = isUrdu ? 'پیغام لکھنا لازمی ہے' : 'Message is required';
    } else if (trimmedMessage.length < 10) {
      messageError = isUrdu ? `کم از کم 10 حروف درکار ہیں (${trimmedMessage.length}/10)` : `Please write at least 10 characters (${trimmedMessage.length}/10)`;
    }

    const isFormValid = isNameValid && isEmailValid && isPhoneValid && isMessageValid;

    return {
      name: { isValid: isNameValid, error: nameError },
      email: { isValid: isEmailValid, error: emailError },
      phone: { isValid: isPhoneValid, error: phoneError },
      message: { isValid: isMessageValid, error: messageError },
      isFormValid,
    };
  }, [formState, isUrdu]);

  const handleBlur = (field: keyof TouchedState) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleChange = (field: keyof FormState, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (!touched[field]) {
      setTouched((prev) => ({ ...prev, [field]: true }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields touched
    setTouched({
      name: true,
      email: true,
      phone: true,
      projectType: true,
      message: true,
    });

    if (!validation.isFormValid) {
      setErrorMessage(isUrdu ? 'براہ کرم فارم کی غلطیاں درست کر کے دوبارہ کوشش کریں۔' : 'Please correct the highlighted fields before submitting.');
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    const FORMSPREE_ID = "xjgvwpoo"; 
    
    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(formState),
      });

      if (response.ok) {
        setStatus('success');
        setFormState({ name: '', email: '', phone: '', projectType: '', message: '' });
        setTouched({ name: false, email: false, phone: false, projectType: false, message: false });
        
        confetti({
          particleCount: 150,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#4f46e5', '#3b82f6', '#ffffff']
        });

        setTimeout(() => setStatus('idle'), 5000);
      } else {
        throw new Error("Transmission Failed");
      }
    } catch (error) {
      console.error("Formspree Error:", error);
      setErrorMessage(t.contact.errorMessage);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <section 
      id="contact" 
      itemScope 
      itemType="https://schema.org/ContactPoint"
      aria-label="Direct Contact & Project Inquiry Channel"
      className="py-16 sm:py-24 bg-white relative overflow-hidden border-t border-slate-200/80 w-full"
    >
      {/* Background Decorative Ambient Lighting */}
      <div className="absolute top-1/3 left-1/4 w-[700px] h-[400px] bg-indigo-100/40 blur-[160px] rounded-full pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[350px] bg-blue-100/30 blur-[150px] rounded-full pointer-events-none" />

      <div className="w-full px-6 sm:px-12 lg:px-16 xl:px-24 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          <div className="lg:col-span-5 space-y-10">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-slate-50 border border-slate-200 rounded-full shadow-2xs">
                <Activity size={14} className="text-indigo-600 animate-pulse" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-700">
                  {t.contact.badge}
                </span>
              </div>
              <h2 className="text-3xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight text-slate-900 leading-tight">
                {t.contact.title}
              </h2>
              <p className="text-slate-600 text-base sm:text-lg font-normal leading-relaxed">
                {t.contact.subtitle}
              </p>
              <div className="pt-6 border-t border-slate-100 space-y-4">
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed font-normal">
                  {isUrdu 
                    ? 'چاہے آپ کے پاس تفصیلی پروجیکٹ خاکہ ہو یا محض ابتدائی خیال، میں آپ کا بہترین ڈیجیٹل سسٹم ڈیزائن کرنے کے لیے تیار ہوں۔'
                    : "Whether you have a detailed project specification or just a preliminary idea, I'm here to help you architect a professional digital solution."}
                </p>
                <ul className="text-slate-600 text-xs uppercase tracking-wider space-y-2 font-bold">
                  <li className="flex items-center gap-2 text-slate-700">
                    <Check size={14} className="text-emerald-600" />
                    <span>{isUrdu ? 'اوسطاً 2 گھنٹے میں فوری جواب' : '2-Hour Average Response Time'}</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <Check size={14} className="text-emerald-600" />
                    <span>{isUrdu ? 'محفوظ و نجی مشاورت (NDA-Ready)' : 'NDA-Ready Consultations'}</span>
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <Check size={14} className="text-emerald-600" />
                    <span>{isUrdu ? 'جامع تکنیکی و لاگت کا منصوبہ' : 'Scoped Technical Proposals'}</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="space-y-6">
                {[
                  { icon: Mail, label: isUrdu ? 'ای میل' : 'Email', value: 'taibejaved485@gmail.com', href: 'mailto:taibejaved485@gmail.com' },
                  { icon: Phone, label: isUrdu ? 'واٹس ایپ / ڈائریکٹ رابطہ' : 'WhatsApp / Direct Call', value: '+92 370 6487654', href: 'https://wa.me/923706487654' },
                  { icon: MapPin, label: isUrdu ? 'مقام' : 'Location', value: 'Ghokhuwal 121 jb Faisalabad, Pakistan', href: '#' }
                ].map((item, i) => (
                  <a key={i} href={item.href} target={item.href.startsWith('http') ? '_blank' : '_self'} rel="noopener noreferrer" className="flex items-center gap-4 sm:gap-6 group p-3.5 rounded-2xl bg-slate-50/80 border border-slate-200/80 hover:border-indigo-400 hover:bg-indigo-50/30 transition-all">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-indigo-600 shadow-2xs group-hover:bg-indigo-600 group-hover:text-white transition-all flex-shrink-0">
                      <item.icon size={20} className="sm:w-6 sm:h-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{item.label}</div>
                      <div className="text-sm sm:text-base font-bold text-slate-900 tracking-tight break-all sm:break-normal group-hover:text-indigo-600 transition-colors">{item.value}</div>
                    </div>
                  </a>
                ))}
            </div>
          </div>

          {/* Form Side (Col 7) */}
          <div className="lg:col-span-7 relative p-6 sm:p-12 bg-slate-50/90 rounded-[2.5rem] border border-slate-200/90 w-full shadow-lg shadow-slate-200/40">
            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              
              {/* Name Field */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-slate-700 ml-1">
                    {t.contact.nameLabel} <span className="text-rose-500">*</span>
                  </label>
                  {touched.name && (
                    <span className="text-[11px] font-semibold flex items-center gap-1 transition-colors">
                      {validation.name.isValid ? (
                        <span className="text-emerald-600 flex items-center gap-1">
                          <Check size={12} /> {isUrdu ? 'درست نام' : 'Valid Name'}
                        </span>
                      ) : (
                        <span className="text-rose-500 flex items-center gap-1">
                          <AlertCircle size={12} /> {validation.name.error}
                        </span>
                      )}
                    </span>
                  )}
                </div>
                <div className="relative">
                  <input 
                    id="name"
                    type="text" 
                    required 
                    name="name"
                    aria-required="true"
                    aria-invalid={touched.name && !validation.name.isValid}
                    placeholder={t.contact.namePlaceholder}
                    value={formState.name} 
                    onChange={(e) => handleChange('name', e.target.value)}
                    onBlur={() => handleBlur('name')}
                    className={`w-full bg-white border rounded-xl px-5 py-4 ${isUrdu ? 'pl-11 pr-5' : 'pr-11 pl-5'} text-slate-900 placeholder:text-slate-400 outline-none transition-all text-sm font-normal shadow-2xs ${
                      touched.name 
                        ? validation.name.isValid 
                          ? 'border-emerald-400 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100' 
                          : 'border-rose-400 focus:border-rose-600 focus:ring-2 focus:ring-rose-100' 
                        : 'border-slate-200 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100'
                    }`}
                  />
                  {touched.name && (
                    <div className={`absolute top-1/2 -translate-y-1/2 pointer-events-none ${isUrdu ? 'left-4' : 'right-4'}`}>
                      {validation.name.isValid ? (
                        <CheckCircle2 size={18} className="text-emerald-500" />
                      ) : (
                        <AlertCircle size={18} className="text-rose-500" />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Email & Phone Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                
                {/* Email Field */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-700 ml-1">
                      {t.contact.emailLabel} <span className="text-rose-500">*</span>
                    </label>
                    {touched.email && (
                      <span className="text-[11px] font-semibold flex items-center gap-1 transition-colors">
                        {validation.email.isValid ? (
                          <span className="text-emerald-600 flex items-center gap-1">
                            <Check size={12} /> {isUrdu ? 'درست ای میل' : 'Valid Email'}
                          </span>
                        ) : (
                          <span className="text-rose-500 flex items-center gap-1">
                            <AlertCircle size={12} /> {isUrdu ? 'غلط' : 'Invalid'}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <input 
                      id="email"
                      type="email" 
                      required 
                      name="email"
                      aria-required="true"
                      aria-invalid={touched.email && !validation.email.isValid}
                      placeholder={t.contact.emailPlaceholder}
                      value={formState.email} 
                      onChange={(e) => handleChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      className={`w-full bg-white border rounded-xl px-5 py-4 ${isUrdu ? 'pl-11 pr-5' : 'pr-11 pl-5'} text-slate-900 placeholder:text-slate-400 outline-none transition-all text-sm font-normal shadow-2xs ${
                        touched.email 
                          ? validation.email.isValid 
                            ? 'border-emerald-400 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100' 
                            : 'border-rose-400 focus:border-rose-600 focus:ring-2 focus:ring-rose-100' 
                          : 'border-slate-200 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100'
                      }`}
                    />
                    {touched.email && (
                      <div className={`absolute top-1/2 -translate-y-1/2 pointer-events-none ${isUrdu ? 'left-4' : 'right-4'}`}>
                        {validation.email.isValid ? (
                          <CheckCircle2 size={18} className="text-emerald-500" />
                        ) : (
                          <AlertCircle size={18} className="text-rose-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {touched.email && !validation.email.isValid && (
                    <p className="text-[11px] text-rose-500 pl-1">{validation.email.error}</p>
                  )}
                </div>

                {/* Phone Field */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-slate-700 ml-1">
                      {t.contact.phoneLabel}
                    </label>
                    {touched.phone && formState.phone.trim() && (
                      <span className="text-[11px] font-semibold flex items-center gap-1 transition-colors">
                        {validation.phone.isValid ? (
                          <span className="text-emerald-600 flex items-center gap-1">
                            <Check size={12} /> {isUrdu ? 'درست' : 'Valid'}
                          </span>
                        ) : (
                          <span className="text-rose-500 flex items-center gap-1">
                            <AlertCircle size={12} /> {isUrdu ? 'غلط' : 'Invalid'}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <input 
                      id="phone"
                      type="tel" 
                      name="phone"
                      placeholder={t.contact.phonePlaceholder}
                      value={formState.phone} 
                      onChange={(e) => handleChange('phone', e.target.value)}
                      onBlur={() => handleBlur('phone')}
                      className={`w-full bg-white border rounded-xl px-5 py-4 ${isUrdu ? 'pl-11 pr-5' : 'pr-11 pl-5'} text-slate-900 placeholder:text-slate-400 outline-none transition-all text-sm font-normal shadow-2xs ${
                        touched.phone && formState.phone.trim()
                          ? validation.phone.isValid 
                            ? 'border-emerald-400 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100' 
                            : 'border-rose-400 focus:border-rose-600 focus:ring-2 focus:ring-rose-100' 
                          : 'border-slate-200 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100'
                      }`}
                    />
                    {touched.phone && formState.phone.trim() && (
                      <div className={`absolute top-1/2 -translate-y-1/2 pointer-events-none ${isUrdu ? 'left-4' : 'right-4'}`}>
                        {validation.phone.isValid ? (
                          <CheckCircle2 size={18} className="text-emerald-500" />
                        ) : (
                          <AlertCircle size={18} className="text-rose-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {touched.phone && formState.phone.trim() && !validation.phone.isValid && (
                    <p className="text-[11px] text-rose-500 pl-1">{validation.phone.error}</p>
                  )}
                </div>
              </div>

              {/* Service Required */}
              <div className="space-y-2">
                <label htmlFor="projectType" className="text-xs font-bold uppercase tracking-wider text-slate-700 ml-1">
                  {t.contact.serviceLabel}
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formState.projectType}
                  aria-label="Select service required"
                  onChange={(e) => handleChange('projectType', e.target.value)}
                  className="w-full bg-white border border-slate-200 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 rounded-xl px-5 py-4 text-slate-900 placeholder:text-slate-400 outline-none transition-all text-sm font-normal shadow-2xs"
                >
                  <option value="">{t.contact.serviceSelectPlaceholder}</option>
                  <option value="wordpress-dev">{isUrdu ? 'ورڈپریس کسٹم ویب سائٹ اور ای کامرس' : 'WordPress Custom Website & E-commerce'}</option>
                  <option value="coded-web-app">{isUrdu ? 'کسٹم کوڈڈ ویب ایپلی کیشن (React / Next.js)' : 'Custom Coded Web Application (React / Next.js)'}</option>
                  <option value="ai-video-editing">{isUrdu ? 'اے آئی ویڈیو ایڈیٹنگ اور وائرل ریلز/شارٹس' : 'AI Video Media & Viral Shorts/Reels Production'}</option>
                  <option value="ui-ux-design">{isUrdu ? 'UI/UX ڈیزائن اور برانڈ گرافک شناخت' : 'UI/UX Design & Brand Graphic Identity'}</option>
                  <option value="other">{isUrdu ? 'عمومی پروجیکٹ مشاورت / دیگر' : 'General Project Inquiry / Technical Consultation'}</option>
                </select>
              </div>

              {/* Message Field */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="message" className="text-xs font-bold uppercase tracking-wider text-slate-700 ml-1">
                    {t.contact.messageLabel} <span className="text-rose-500">*</span>
                  </label>
                  <span className="text-[11px] font-semibold flex items-center gap-1.5">
                    {touched.message ? (
                      validation.message.isValid ? (
                        <span className="text-emerald-600 flex items-center gap-1">
                          <Check size={12} /> {formState.message.trim().length} {isUrdu ? 'حروف' : 'chars'}
                        </span>
                      ) : (
                        <span className="text-rose-500 flex items-center gap-1">
                          <AlertCircle size={12} /> {formState.message.trim().length}/10 {isUrdu ? 'کم از کم حروف' : 'chars min'}
                        </span>
                      )
                    ) : (
                      <span className="text-slate-400 text-[11px]">{isUrdu ? 'کم از کم 10 حروف' : 'Min 10 characters'}</span>
                    )}
                  </span>
                </div>
                <div className="relative">
                  <textarea 
                    id="message"
                    required 
                    name="message"
                    aria-required="true"
                    aria-invalid={touched.message && !validation.message.isValid}
                    rows={4} 
                    placeholder={t.contact.messagePlaceholder}
                    value={formState.message} 
                    onChange={(e) => handleChange('message', e.target.value)}
                    onBlur={() => handleBlur('message')}
                    className={`w-full bg-white border rounded-xl px-5 py-4 text-slate-900 placeholder:text-slate-400 outline-none transition-all resize-none text-sm font-normal shadow-2xs ${
                      touched.message 
                        ? validation.message.isValid 
                          ? 'border-emerald-400 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100' 
                          : 'border-rose-400 focus:border-rose-600 focus:ring-2 focus:ring-rose-100' 
                        : 'border-slate-200 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100'
                    }`}
                  ></textarea>
                </div>
                {touched.message && !validation.message.isValid && (
                  <p className="text-[11px] text-rose-500 pl-1">{validation.message.error}</p>
                )}
              </div>

              {/* Status Alert Banner if Error */}
              {status === 'error' && (
                <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-2.5 text-rose-700 text-xs font-semibold">
                  <AlertCircle size={16} className="flex-shrink-0 text-rose-600" />
                  <span>{errorMessage || (isUrdu ? 'براہ کرم تمام لازمی فیلڈز چیک کر کے دوبارہ کوشش کریں۔' : 'Please check all required fields and try again.')}</span>
                </div>
              )}

              {/* Status Alert Banner if Success */}
              {status === 'success' && (
                <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2.5 text-emerald-700 text-xs font-semibold">
                  <CheckCircle2 size={16} className="flex-shrink-0 text-emerald-600" />
                  <span>{t.contact.successMessage}</span>
                </div>
              )}

              {/* Submit Button */}
              <button 
                type="submit" 
                disabled={status === 'loading' || status === 'success'}
                className={`w-full group relative flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-xs uppercase tracking-[0.2em] transition-all active:scale-[0.98] disabled:opacity-75 shadow-md cursor-pointer ${
                  status === 'success' 
                    ? 'bg-emerald-600 text-white' 
                    : status === 'error'
                    ? 'bg-rose-600 text-white hover:bg-rose-700'
                    : validation.isFormValid
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-200 shadow-lg'
                    : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
              >
                {status === 'loading' && (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{t.contact.btnSending}</span>
                  </>
                )}
                {status === 'success' && (
                  <>
                    <span>{t.contact.successTitle}</span>
                    <ShieldCheck size={18} />
                  </>
                )}
                {status === 'error' && (
                  <>
                    <span>{isUrdu ? 'ارسال میں خرابی۔ دوبارہ کوشش کریں' : 'Submission Failed. Retry'}</span>
                    <AlertCircle size={18} />
                  </>
                )}
                {status === 'idle' && (
                  <>
                    <span>{t.contact.btnSend}</span>
                    <Send size={16} className={`transition-transform ${isUrdu ? 'group-hover:-translate-x-1 rotate-180' : 'group-hover:translate-x-1'}`} />
                  </>
                )}
              </button>

              {/* Live Form Completeness Indicator */}
              <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 px-1">
                <span>
                  {validation.isFormValid ? (
                    <span className="text-emerald-600 font-semibold flex items-center gap-1">
                      <CheckCircle2 size={12} /> {isUrdu ? 'تمام لازمی فیلڈز مکمل ہیں' : 'All required fields complete'}
                    </span>
                  ) : (
                    <span>{isUrdu ? <>فیلڈز جن پر <span className="text-rose-500 font-bold">*</span> ہے وہ لازمی ہیں</> : <>Fields marked with <span className="text-rose-500 font-bold">*</span> are required</>}</span>
                  )}
                </span>
                <span className="font-medium text-slate-400">{isUrdu ? 'محفوظ 256-bit اینکرپٹڈ' : 'Secure 256-bit Encrypted'}</span>
              </div>

            </form>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Contact;